import type { WindowEventModel } from '@branchjs/communicator';
import { ADS_STATS_COMMUNICATION_TOPICS, GENERAL_COMMUNICATON_TOPICS } from '@branchjs/communicator';
import { theme } from '@branchjs/neo-components';
import React, { useContext, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import dash_api from '../../../main/support/dash_api';
import NEOCircularProgress from '../../components/NEOCircularProgress';
import MicroFrontendV2 from '../../components/micro-frontends/MicroFrontendV2';
import { useMicroFrontendLoadAnalytics } from '../../hooks/micro-frontends/useMicroFrontendLoadAnalytics';
import { useMicroFrontendTimeout } from '../../hooks/micro-frontends/useMicroFrontendTimeout';
import { CommunicationContext } from '../../stores/CommunicatorContext';
import Pendo from '../../support/pendo';
import { ScreenDiscoDashboardStyles } from './ScreenDiscoDashboard.styles';

export const SCREEN_DISCO_DASHBOARD_COLOR = theme.colors.gray[0];

type ScreenDiscoDashboardProps = {
  url: string;
};

const ScreenDiscoDashboard = ({ url }: ScreenDiscoDashboardProps) => {
  const branchPendo = new Pendo();
  const [isMicroFrontendReady, setIsMicroFrontendReady] = useState<boolean>(false);

  const { showBoundary } = useErrorBoundary();
  const { communicator } = useContext(CommunicationContext);

  useMicroFrontendTimeout(isMicroFrontendReady, 'disco-dashboard');

  useMicroFrontendLoadAnalytics(isMicroFrontendReady, 'disco-dashboard');

  const { GET_PENDO_USER, COMMUNICATE_PENDO_USER } = GENERAL_COMMUNICATON_TOPICS;

  const { INIT_COMPLETED, RENDER_ERROR } = ADS_STATS_COMMUNICATION_TOPICS;

  const setupSubscriptions = () => {
    const initCompletedSubscription = communicator.on(INIT_COMPLETED, () => {
      setIsMicroFrontendReady(true);
    });

    const renderErrorSubscription = communicator.on(RENDER_ERROR, (windowEvent: WindowEventModel) => {
      showBoundary(windowEvent.data);
    });

    const pendoSubscription = communicator.on(GET_PENDO_USER, async () => {
      try {
        const view = (window.App?.context as any)?.view;
        const { data: userData } = await dash_api.get('/v1/dashboard-user/by-cookie');
        const { id, email, name } = userData;
        const { data: organization } = await dash_api.get('/v1/org');
        const config = {
          userId: id,
          email,
          name,
          OnboardingStep: undefined,
          isAgencyUser: false,
          currentApp: undefined,
          currentOrg: organization,
          currentAgency: undefined,
          role: undefined,
        };
        setTimeout(() => {
          console.log('COMMUNICATING PENDO USER');
          branchPendo.initialize(config);
          communicator.send(COMMUNICATE_PENDO_USER, { ...config, view });
        }, 1000);
      } catch {
        console.warn('Unable to initialize Pendo');
      }
    });

    return () => {
      initCompletedSubscription.unsubscribe();
      renderErrorSubscription.unsubscribe();
      pendoSubscription.unsubscribe();
    };
  };

  useEffect(setupSubscriptions, []);

  return (
    <div className={ScreenDiscoDashboardStyles.container}>
      <MicroFrontendV2 name="disco-dashboard" url={url} modal={true} isLoading={!isMicroFrontendReady} />
      {!isMicroFrontendReady && <NEOCircularProgress />}
    </div>
  );
};

export default ScreenDiscoDashboard;
